import React, { useState, useEffect, useCallback } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { DATE_DISPLAY_FORMAT, toApiDate } from "../../utils/dateUtils";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Divider,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  InputAdornment,
  FormHelperText,
  FormControlLabel,
  Switch,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Edit,
  Close,
  Add,
  Delete,
  CalendarMonth,
  School,
  Event,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import api from "../../services/api";
import Toast from "../../components/common/Toast";

const AcademicInfo = () => {
  const { selectedInstitute, isInInstituteContext } = useSelector(
    (state) => state.instituteContext
  );
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  });
  const userRole = (user?.role || localStorage.getItem("userRole") || "").toLowerCase();
  const normalizedRole = userRole === "institute" ? "admin" : userRole;
  const isSuperAdmin = normalizedRole === "superadmin";
  const effectiveInstituteId =
    isSuperAdmin && isInInstituteContext
      ? selectedInstitute?.id
      : user?.instituteId ?? user?.institute?.id ?? null;

  const [activeTab, setActiveTab] = useState(0);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState("");

  // Academic Years API state (only tab 0)
  const [academicYearsFromApi, setAcademicYearsFromApi] = useState([]);
  const [loadingAcademicYears, setLoadingAcademicYears] = useState(false);
  const [yearPagination, setYearPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [yearSearch, setYearSearch] = useState("");
  const [yearSearchDebounced, setYearSearchDebounced] = useState("");
  const [yearSort, setYearSort] = useState("id");
  const [yearOrder, setYearOrder] = useState("DESC");
  const [yearFormData, setYearFormData] = useState({
    academicYear: "",
    startDate: "",
    endDate: "",
    status: "active",
  });
  const [yearFormErrors, setYearFormErrors] = useState({});
  const [selectedYearForEdit, setSelectedYearForEdit] = useState(null);
  const [deleteYearDialogOpen, setDeleteYearDialogOpen] = useState(false);
  const [selectedYearForDelete, setSelectedYearForDelete] = useState(null);
  const [submittingYear, setSubmittingYear] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastSeverity, setToastSeverity] = useState("success");

  const [academicInfoStats, setAcademicInfoStats] = useState(null);

  // Ensure date input year is exactly 4 digits (1000-9999); reject invalid keyboard input
  const sanitizeDateYear = (value) => {
    if (value == null || value === "") return "";
    const s = String(value).trim();
    if (!s) return "";
    const parts = s.split("-");
    if (parts.length < 1) return "";
    const yearStr = parts[0];
    if (!/^\d+$/.test(yearStr) || yearStr.length !== 4) return "";
    const year = parseInt(yearStr, 10);
    if (year < 1000 || year > 9999) return "";
    return s;
  };
  const isIsoDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || "").trim());
  const MIN_ISO_DATE = "1000-01-01";
  const MAX_ISO_DATE = "9999-12-31";
  const dateRangeError = (v, minIso, maxIso, label) => {
    const s = String(v || "").trim();
    if (!isIsoDate(s)) return `${label} must be in YYYY-MM-DD format`;
    if (minIso && s < minIso) return `${label} must be on or after ${minIso}`;
    if (maxIso && s > maxIso) return `${label} must be on or before ${maxIso}`;
    return "";
  };

  const handleDateChange = (value, field, setter) => {
    const v = (field === "startDate" || field === "endDate" || field === "date") ? sanitizeDateYear(value) : value;
    setter((prev) => ({ ...prev, [field]: v }));
  };

  const NAME_MIN_LENGTH = 2;
  const NAME_MAX_LENGTH = 150;
  const blockNumberKey = (e) => {
    if (/\d/.test(e.key)) e.preventDefault();
  };
  const sanitizeNameNoNumbers = (value) => {
    const s = String(value ?? "").replace(/\d/g, "");
    return s.length > NAME_MAX_LENGTH ? s.slice(0, NAME_MAX_LENGTH) : s;
  };

  const fetchAcademicInfoStats = useCallback(async () => {
    try {
      const res = await api.get("/dashboard/academic-info-stats");
      const data = res?.data?.data ?? res?.data ?? {};
      setAcademicInfoStats(data);
    } catch (err) {
      console.error("Failed to fetch academic info stats", err);
      setAcademicInfoStats(null);
    }
  }, []);

  useEffect(() => {
    fetchAcademicInfoStats();
  }, [fetchAcademicInfoStats]);

  useEffect(() => {
    const t = setTimeout(() => setYearSearchDebounced(yearSearch), 400);
    return () => clearTimeout(t);
  }, [yearSearch]);

  const fetchAcademicYears = useCallback(async () => {
    setLoadingAcademicYears(true);
    try {
      const params = new URLSearchParams({
        page: String(yearPagination.page),
        limit: String(yearPagination.limit),
        sort: yearSort,
        order: yearOrder,
      });
      if (yearSearchDebounced) params.set("search", yearSearchDebounced);
      if (effectiveInstituteId && (isSuperAdmin || normalizedRole === "admin"))
        params.set("instituteId", String(effectiveInstituteId));
      const res = await api.get(`/academic-info/all?${params.toString()}`);
      const data = res?.data?.data ?? res?.data ?? {};
      const list = data?.academicYears ?? [];
      const pag = data?.pagination ?? {};
      setAcademicYearsFromApi(list);
      setYearPagination((prev) => ({ ...prev, total: pag.total ?? 0 }));
    } catch (err) {
      console.error("Failed to fetch academic years", err);
      setToastMessage(err?.response?.data?.message || "Failed to load academic years.");
      setToastSeverity("error");
      setAcademicYearsFromApi([]);
    } finally {
      setLoadingAcademicYears(false);
    }
  }, [
    yearPagination.page,
    yearPagination.limit,
    yearSort,
    yearOrder,
    yearSearchDebounced,
    effectiveInstituteId,
    isSuperAdmin,
    normalizedRole,
  ]);

  useEffect(() => {
    if (activeTab === 0) fetchAcademicYears();
  }, [activeTab, fetchAcademicYears]);

  const academicYearsRows = academicYearsFromApi.map((r) => ({
    ...r,
    year: r.academicYear,
    semesters: r.semesters ?? "—",
  }));

  // Alias for compatibility (e.g. stats or any code expecting "academicYears")
  const academicYears = academicYearsRows;

  // Semesters API state (tab 1)
  const [semestersFromApi, setSemestersFromApi] = useState([]);
  const [loadingSemesters, setLoadingSemesters] = useState(false);
  const [semesterPagination, setSemesterPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [semesterSearch, setSemesterSearch] = useState("");
  const [semesterSearchDebounced, setSemesterSearchDebounced] = useState("");
  const [semesterSort, setSemesterSort] = useState("id");
  const [semesterOrder, setSemesterOrder] = useState("DESC");
  const [semesterAcademicYearIdFilter, setSemesterAcademicYearIdFilter] = useState("");
  const [academicYearsForDropdown, setAcademicYearsForDropdown] = useState([]);
  const [semesterFormData, setSemesterFormData] = useState({
    semesterName: "",
    academicYearId: "",
    startDate: "",
    endDate: "",
    status: "active",
  });
  const [semesterFormErrors, setSemesterFormErrors] = useState({});
  const [selectedSemesterForEdit, setSelectedSemesterForEdit] = useState(null);
  const [deleteSemesterDialogOpen, setDeleteSemesterDialogOpen] = useState(false);
  const [selectedSemesterForDelete, setSelectedSemesterForDelete] = useState(null);
  const [submittingSemester, setSubmittingSemester] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSemesterSearchDebounced(semesterSearch), 400);
    return () => clearTimeout(t);
  }, [semesterSearch]);

  const fetchAcademicYearsForDropdown = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: "1", limit: "100", sort: "startDate", order: "DESC" });
      if (effectiveInstituteId && (isSuperAdmin || normalizedRole === "admin"))
        params.set("instituteId", String(effectiveInstituteId));
      const res = await api.get(`/academic-info/all?${params.toString()}`);
      const data = res?.data?.data ?? res?.data ?? {};
      setAcademicYearsForDropdown(data?.academicYears ?? []);
    } catch (err) {
      console.error("Failed to fetch academic years for dropdown", err);
      setAcademicYearsForDropdown([]);
    }
  }, [effectiveInstituteId, isSuperAdmin, normalizedRole]);

  const fetchSemesters = useCallback(async () => {
    setLoadingSemesters(true);
    try {
      const params = new URLSearchParams({
        page: String(semesterPagination.page),
        limit: String(semesterPagination.limit),
        sort: semesterSort,
        order: semesterOrder,
      });
      if (semesterSearchDebounced) params.set("search", semesterSearchDebounced);
      if (semesterAcademicYearIdFilter) params.set("academicYearId", semesterAcademicYearIdFilter);
      const res = await api.get(`/academic-info/semester/all?${params.toString()}`);
      const data = res?.data?.data ?? res?.data ?? {};
      const list = data?.semesters ?? [];
      const pag = data?.pagination ?? {};
      setSemestersFromApi(list);
      setSemesterPagination((prev) => ({ ...prev, total: pag.total ?? 0 }));
    } catch (err) {
      console.error("Failed to fetch semesters", err);
      setToastMessage(err?.response?.data?.message || "Failed to load semesters.");
      setToastSeverity("error");
      setSemestersFromApi([]);
    } finally {
      setLoadingSemesters(false);
    }
  }, [
    semesterPagination.page,
    semesterPagination.limit,
    semesterSort,
    semesterOrder,
    semesterSearchDebounced,
    semesterAcademicYearIdFilter,
  ]);

  useEffect(() => {
    if (activeTab === 1) {
      fetchSemesters();
      fetchAcademicYearsForDropdown();
    }
  }, [activeTab, fetchSemesters, fetchAcademicYearsForDropdown]);

  const semestersRows = semestersFromApi.map((r) => ({
    ...r,
    name: r.semesterName ?? r.name,
    academicYear: r.academicYear?.academicYear ?? r.academicYear ?? "—",
  }));
  const todayIso = new Date().toISOString().split("T")[0];

  // Holidays API state (tab 2)
  const [holidaysFromApi, setHolidaysFromApi] = useState([]);
  const [loadingHolidays, setLoadingHolidays] = useState(false);
  const [holidayPagination, setHolidayPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [holidaySearch, setHolidaySearch] = useState("");
  const [holidaySearchDebounced, setHolidaySearchDebounced] = useState("");
  const [holidaySort, setHolidaySort] = useState("id");
  const [holidayOrder, setHolidayOrder] = useState("DESC");
  const [holidayUpcomingOnly, setHolidayUpcomingOnly] = useState(false);
  const [upcomingHolidaysCount, setUpcomingHolidaysCount] = useState(null);
  const [holidayFormData, setHolidayFormData] = useState({
    name: "",
    startDate: "",
    endDate: "",
    type: "National",
    description: "",
  });
  const [holidayFormErrors, setHolidayFormErrors] = useState({});
  const [selectedHolidayForEdit, setSelectedHolidayForEdit] = useState(null);
  const [deleteHolidayDialogOpen, setDeleteHolidayDialogOpen] = useState(false);
  const [selectedHolidayForDelete, setSelectedHolidayForDelete] = useState(null);
  const [submittingHoliday, setSubmittingHoliday] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setHolidaySearchDebounced(holidaySearch), 400);
    return () => clearTimeout(t);
  }, [holidaySearch]);

  const fetchUpcomingHolidaysCount = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (effectiveInstituteId && (isSuperAdmin || normalizedRole === "admin"))
        params.set("instituteId", String(effectiveInstituteId));
      const res = await api.get(`/academic-info/holidays/upcoming-count?${params.toString()}`);
      const data = res?.data?.data ?? res?.data ?? {};
      const count = data?.count ?? data?.upcomingCount ?? (Array.isArray(data) ? data.length : null);
      setUpcomingHolidaysCount(count != null ? count : null);
    } catch (err) {
      console.error("Failed to fetch upcoming holidays count", err);
      setUpcomingHolidaysCount(null);
    }
  }, [effectiveInstituteId, isSuperAdmin, normalizedRole]);

  const fetchHolidays = useCallback(async () => {
    setLoadingHolidays(true);
    try {
      const params = new URLSearchParams({
        page: String(holidayPagination.page),
        limit: String(holidayPagination.limit),
        sort: holidaySort,
        order: holidayOrder,
      });
      if (holidaySearchDebounced) params.set("search", holidaySearchDebounced);
      if (holidayUpcomingOnly) params.set("upcoming", "true");
      if (effectiveInstituteId && (isSuperAdmin || normalizedRole === "admin"))
        params.set("instituteId", String(effectiveInstituteId));
      const res = await api.get(`/academic-info/holidays/all?${params.toString()}`);
      const data = res?.data?.data ?? res?.data ?? {};
      const list = data?.holidays ?? data?.data ?? [];
      const pag = data?.pagination ?? {};
      setHolidaysFromApi(Array.isArray(list) ? list : []);
      setHolidayPagination((prev) => ({ ...prev, total: pag.total ?? 0 }));
    } catch (err) {
      console.error("Failed to fetch holidays", err);
      setToastMessage(err?.response?.data?.message || "Failed to load holidays.");
      setToastSeverity("error");
      setHolidaysFromApi([]);
    } finally {
      setLoadingHolidays(false);
    }
  }, [
    holidayPagination.page,
    holidayPagination.limit,
    holidaySort,
    holidayOrder,
    holidaySearchDebounced,
    holidayUpcomingOnly,
    effectiveInstituteId,
    isSuperAdmin,
    normalizedRole,
  ]);

  useEffect(() => {
    if (activeTab === 2) {
      fetchHolidays();
    }
    fetchUpcomingHolidaysCount();
  }, [activeTab, fetchHolidays, fetchUpcomingHolidaysCount]);

  const normalizeHolidayType = (value) => {
    if (!value || typeof value !== "string") return "National";
    const v = value.trim().toLowerCase();
    if (v === "national") return "National";
    if (v === "festival") return "Festival";
    if (v === "religious") return "Religious";
    if (v === "institutional") return "Institutional";
    if (v === "public") return "Public";
    if (v === "other") return "Other";
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  };

  const holidaysRows = holidaysFromApi.map((r) => ({
    ...r,
    date: r.startDate ?? r.date,
    type: normalizeHolidayType(r.type),
  }));

  // Events API state (tab 3)
  const [eventsFromApi, setEventsFromApi] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [eventPagination, setEventPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [eventSearch, setEventSearch] = useState("");
  const [eventSearchDebounced, setEventSearchDebounced] = useState("");
  const [eventSort, setEventSort] = useState("id");
  const [eventOrder, setEventOrder] = useState("DESC");
  const [eventUpcomingOnly, setEventUpcomingOnly] = useState(false);
  const [upcomingEventsCount, setUpcomingEventsCount] = useState(null);
  const [eventFormData, setEventFormData] = useState({
    name: "",
    startDate: "",
    type: "Cultural",
    location: "",
    description: "",
  });
  const [eventFormErrors, setEventFormErrors] = useState({});
  const [selectedEventForEdit, setSelectedEventForEdit] = useState(null);
  const [deleteEventDialogOpen, setDeleteEventDialogOpen] = useState(false);
  const [selectedEventForDelete, setSelectedEventForDelete] = useState(null);
  const [submittingEvent, setSubmittingEvent] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setEventSearchDebounced(eventSearch), 400);
    return () => clearTimeout(t);
  }, [eventSearch]);

  const fetchUpcomingEventsCount = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (effectiveInstituteId && (isSuperAdmin || normalizedRole === "admin"))
        params.set("instituteId", String(effectiveInstituteId));
      const res = await api.get(`/academic-info/events/upcoming-count?${params.toString()}`);
      const data = res?.data?.data ?? res?.data ?? {};
      const count = data?.count ?? data?.upcomingCount ?? (Array.isArray(data) ? data.length : null);
      setUpcomingEventsCount(count != null ? count : null);
    } catch (err) {
      console.error("Failed to fetch upcoming events count", err);
      setUpcomingEventsCount(null);
    }
  }, [effectiveInstituteId, isSuperAdmin, normalizedRole]);

  const fetchEvents = useCallback(async () => {
    setLoadingEvents(true);
    try {
      const params = new URLSearchParams({
        page: String(eventPagination.page),
        limit: String(eventPagination.limit),
        sort: eventSort,
        order: eventOrder,
      });
      if (eventSearchDebounced) params.set("search", eventSearchDebounced);
      if (eventUpcomingOnly) params.set("upcoming", "true");
      if (effectiveInstituteId && (isSuperAdmin || normalizedRole === "admin"))
        params.set("instituteId", String(effectiveInstituteId));
      const res = await api.get(`/academic-info/events/all?${params.toString()}`);
      const data = res?.data?.data ?? res?.data ?? {};
      const list = data?.events ?? data?.data ?? [];
      const pag = data?.pagination ?? {};
      setEventsFromApi(Array.isArray(list) ? list : []);
      setEventPagination((prev) => ({ ...prev, total: pag.total ?? 0 }));
    } catch (err) {
      console.error("Failed to fetch events", err);
      setToastMessage(err?.response?.data?.message || "Failed to load events.");
      setToastSeverity("error");
      setEventsFromApi([]);
    } finally {
      setLoadingEvents(false);
    }
  }, [
    eventPagination.page,
    eventPagination.limit,
    eventSort,
    eventOrder,
    eventSearchDebounced,
    eventUpcomingOnly,
    effectiveInstituteId,
    isSuperAdmin,
    normalizedRole,
  ]);

  useEffect(() => {
    if (activeTab === 3) {
      fetchEvents();
    }
    fetchUpcomingEventsCount();
  }, [activeTab, fetchEvents, fetchUpcomingEventsCount]);

  const normalizeEventType = (value) => {
    if (!value || typeof value !== "string") return "Cultural";
    const v = value.trim().toLowerCase();
    if (v === "cultural") return "Cultural";
    if (v === "technical") return "Technical";
    if (v === "sports") return "Sports";
    if (v === "academic") return "Academic";
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  };

  const eventsRows = eventsFromApi.map((r) => ({
    ...r,
    date: r.startDate ?? r.date,
    type: normalizeEventType(r.type),
  }));

  const [examTypeState, setExamTypeState] = useState({
    rows: [],
    loading: false,
    form: { name: "", color: "", previousKey: "" },
    errors: {},
    selectedForEdit: null,
    deleteDialogOpen: false,
    selectedForDelete: null,
    submitting: false,
  });

  const [notificationTypeState, setNotificationTypeState] = useState({
    rows: [],
    loading: false,
    form: { name: "", previousKey: "" },
    errors: {},
    selectedForEdit: null,
    deleteDialogOpen: false,
    selectedForDelete: null,
    submitting: false,
  });

  const fetchExamTypes = useCallback(async () => {
    setExamTypeState((p) => ({ ...p, loading: true }));
    try {
      const params = new URLSearchParams();
      if (effectiveInstituteId && (isSuperAdmin || normalizedRole === "admin")) params.set("instituteId", String(effectiveInstituteId));
      const res = await api.get(`/academic-info/exam-types/all?${params.toString()}`);
      const list = res?.data?.data ?? res?.data ?? [];
      setExamTypeState((p) => ({ ...p, rows: Array.isArray(list) ? list : [] }));
    } catch (err) {
      setToastMessage(err?.response?.data?.message || "Failed to load exam types.");
      setToastSeverity("error");
      setExamTypeState((p) => ({ ...p, rows: [] }));
    } finally {
      setExamTypeState((p) => ({ ...p, loading: false }));
    }
  }, [effectiveInstituteId, isSuperAdmin, normalizedRole]);

  const fetchNotificationTypes = useCallback(async () => {
    setNotificationTypeState((p) => ({ ...p, loading: true }));
    try {
      const params = new URLSearchParams();
      if (effectiveInstituteId && (isSuperAdmin || normalizedRole === "admin")) params.set("instituteId", String(effectiveInstituteId));
      const res = await api.get(`/academic-info/notification-types/all?${params.toString()}`);
      const list = res?.data?.data ?? res?.data ?? [];
      setNotificationTypeState((p) => ({ ...p, rows: Array.isArray(list) ? list : [] }));
    } catch (err) {
      setToastMessage(err?.response?.data?.message || "Failed to load notification types.");
      setToastSeverity("error");
      setNotificationTypeState((p) => ({ ...p, rows: [] }));
    } finally {
      setNotificationTypeState((p) => ({ ...p, loading: false }));
    }
  }, [effectiveInstituteId, isSuperAdmin, normalizedRole]);

  useEffect(() => {
    if (activeTab === 4) fetchExamTypes();
    if (activeTab === 5) fetchNotificationTypes();
  }, [activeTab, fetchExamTypes, fetchNotificationTypes]);

  const yearColumns = [
    {
      field: "year",
      headerName: "Academic Year",
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar sx={{ bgcolor: "primary.light", width: 32, height: 32 }}>
            <CalendarMonth fontSize="small" />
          </Avatar>
          <Typography variant="body2" fontWeight={500}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    { field: "startDate", headerName: "Start Date", width: 160 },
    { field: "endDate", headerName: "End Date", width: 160 },
    // { field: "semesters", headerName: "Semesters", width: 140 },
    {
      field: "status",
      headerName: "Status",
      width: 160,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === "active" ? "success" : "default"}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 160,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleEditYearOpen(params.row)}
            title="Edit"
          >
            <Edit />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDeleteYearOpen(params.row)}
            title="Delete"
          >
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  const validateYearForm = () => {
    const err = {};
    const yearRegex = /^\d{4}-\d{4}$/;
    if (!(yearFormData.academicYear || "").trim()) {
      err.academicYear = "Academic year is required (e.g. 2024-2025)";
    } else if (!yearRegex.test(yearFormData.academicYear.trim())) {
      err.academicYear = "Format must be YYYY-YYYY";
    }
    if (!(yearFormData.startDate || "").trim()) err.startDate = "Start date is required";
    else if (sanitizeDateYear(yearFormData.startDate) !== yearFormData.startDate) err.startDate = "Year must be 4 digits (1000-9999)";
    else {
      const msg = dateRangeError(yearFormData.startDate, MIN_ISO_DATE, MAX_ISO_DATE, "Start date");
      if (msg) err.startDate = msg;
    }
    if (!(yearFormData.endDate || "").trim()) err.endDate = "End date is required";
    else if (sanitizeDateYear(yearFormData.endDate) !== yearFormData.endDate) err.endDate = "Year must be 4 digits (1000-9999)";
    else {
      const msg = dateRangeError(yearFormData.endDate, MIN_ISO_DATE, MAX_ISO_DATE, "End date");
      if (msg) err.endDate = msg;
    }
    if (yearFormData.startDate && yearFormData.endDate && new Date(yearFormData.endDate) <= new Date(yearFormData.startDate)) {
      err.endDate = "End date must be after start date";
    }
    setYearFormErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleAddYearOpen = () => {
    setSelectedYearForEdit(null);
    setYearFormData({ academicYear: "", startDate: "", endDate: "", status: "active" });
    setYearFormErrors({});
    setDialogType("year");
    setAddDialogOpen(true);
  };

  const handleEditYearOpen = (row) => {
    setSelectedYearForEdit(row);
    setYearFormData({
      academicYear: row.academicYear || row.year || "",
      startDate: row.startDate || "",
      endDate: row.endDate || "",
      status: row.status || "active",
    });
    setYearFormErrors({});
    setDialogType("year");
    setAddDialogOpen(true);
  };

  const handleCreateYear = async () => {
    if (!validateYearForm()) return;
    setSubmittingYear(true);
    try {
      const body = {
        academicYear: yearFormData.academicYear.trim(),
        startDate: yearFormData.startDate,
        endDate: yearFormData.endDate,
        status: yearFormData.status || "active",
      };
      if (effectiveInstituteId && (isSuperAdmin || normalizedRole === "admin"))
        body.instituteId = Number(effectiveInstituteId);
      const res = await api.post("/academic-info/create", body);
      setToastMessage(res?.data?.message || "Academic year created successfully.");
      setToastSeverity("success");
      setAddDialogOpen(false);
      fetchAcademicYears();
      fetchAcademicInfoStats();
    } catch (err) {
      setToastMessage(err?.response?.data?.message || "Failed to create academic year.");
      setToastSeverity("error");
    } finally {
      setSubmittingYear(false);
    }
  };

  const handleUpdateYear = async () => {
    if (!selectedYearForEdit?.id || !validateYearForm()) return;
    setSubmittingYear(true);
    try {
      const body = {
        academicYear: yearFormData.academicYear.trim(),
        startDate: yearFormData.startDate,
        endDate: yearFormData.endDate,
        status: yearFormData.status,
      };
      const res = await api.put(`/academic-info/update?id=${selectedYearForEdit.id}`, body);
      setToastMessage(res?.data?.message || "Academic year updated successfully.");
      setToastSeverity("success");
      setAddDialogOpen(false);
      setSelectedYearForEdit(null);
      fetchAcademicYears();
      fetchAcademicInfoStats();
    } catch (err) {
      setToastMessage(err?.response?.data?.message || "Failed to update academic year.");
      setToastSeverity("error");
    } finally {
      setSubmittingYear(false);
    }
  };

  const handleDeleteYearOpen = (row) => {
    setSelectedYearForDelete(row);
    setDeleteYearDialogOpen(true);
  };

  const handleDeleteYear = async () => {
    if (!selectedYearForDelete?.id) return;
    setSubmittingYear(true);
    try {
      await api.delete(`/academic-info/delete?id=${selectedYearForDelete.id}`);
      setToastMessage("Academic year deleted successfully.");
      setToastSeverity("success");
      setDeleteYearDialogOpen(false);
      setSelectedYearForDelete(null);
      fetchAcademicYears();
      fetchAcademicInfoStats();
    } catch (err) {
      setToastMessage(err?.response?.data?.message || "Failed to delete academic year.");
      setToastSeverity("error");
    } finally {
      setSubmittingYear(false);
    }
  };

  const validateExamTypeForm = () => {
    const err = {};
    if (!(examTypeState.form.name || "").trim()) err.name = "Label is required";
    setExamTypeState((p) => ({ ...p, errors: err }));
    return Object.keys(err).length === 0;
  };

  const handleUpsertExamType = async () => {
    if (!validateExamTypeForm()) return;
    setExamTypeState((p) => ({ ...p, submitting: true }));
    try {
      const body = { label: examTypeState.form.name.trim() };
      if (examTypeState.form.color) body.color = examTypeState.form.color;
      if (examTypeState.form.previousKey) body.previousKey = examTypeState.form.previousKey;
      if (effectiveInstituteId && (isSuperAdmin || normalizedRole === "admin")) body.instituteId = Number(effectiveInstituteId);
      const res = await api.post("/academic-info/exam-types/upsert", body);
      setToastMessage(res?.data?.message || "Exam type saved.");
      setToastSeverity("success");
      setAddDialogOpen(false);
      setExamTypeState((p) => ({ ...p, selectedForEdit: null, form: { name: "", color: "", previousKey: "" } }));
      fetchExamTypes();
    } catch (err) {
      setToastMessage(err?.response?.data?.message || "Failed to save exam type.");
      setToastSeverity("error");
    } finally {
      setExamTypeState((p) => ({ ...p, submitting: false }));
    }
  };

  const handleDeleteExamType = async () => {
    if (!examTypeState.selectedForDelete?.key) return;
    setExamTypeState((p) => ({ ...p, submitting: true }));
    try {
      const qp = new URLSearchParams({ key: examTypeState.selectedForDelete.key });
      if (effectiveInstituteId && (isSuperAdmin || normalizedRole === "admin")) qp.set("instituteId", String(effectiveInstituteId));
      await api.delete(`/academic-info/exam-types/delete?${qp.toString()}`);
      setToastMessage("Exam type deleted.");
      setToastSeverity("success");
      setExamTypeState((p) => ({ ...p, deleteDialogOpen: false, selectedForDelete: null }));
      fetchExamTypes();
    } catch (err) {
      setToastMessage(err?.response?.data?.message || "Failed to delete exam type.");
      setToastSeverity("error");
    } finally {
      setExamTypeState((p) => ({ ...p, submitting: false }));
    }
  };

  const validateNotificationTypeForm = () => {
    const err = {};
    if (!(notificationTypeState.form.name || "").trim()) err.name = "Label is required";
    setNotificationTypeState((p) => ({ ...p, errors: err }));
    return Object.keys(err).length === 0;
  };

  const handleUpsertNotificationType = async () => {
    if (!validateNotificationTypeForm()) return;
    setNotificationTypeState((p) => ({ ...p, submitting: true }));
    try {
      const body = { label: notificationTypeState.form.name.trim() };
      if (notificationTypeState.form.previousKey) body.previousKey = notificationTypeState.form.previousKey;
      if (effectiveInstituteId && (isSuperAdmin || normalizedRole === "admin")) body.instituteId = Number(effectiveInstituteId);
      const res = await api.post("/academic-info/notification-types/upsert", body);
      setToastMessage(res?.data?.message || "Notification type saved.");
      setToastSeverity("success");
      setAddDialogOpen(false);
      setNotificationTypeState((p) => ({ ...p, selectedForEdit: null, form: { name: "", previousKey: "" } }));
      fetchNotificationTypes();
    } catch (err) {
      setToastMessage(err?.response?.data?.message || "Failed to save notification type.");
      setToastSeverity("error");
    } finally {
      setNotificationTypeState((p) => ({ ...p, submitting: false }));
    }
  };

  const handleDeleteNotificationType = async () => {
    if (!notificationTypeState.selectedForDelete?.key) return;
    setNotificationTypeState((p) => ({ ...p, submitting: true }));
    try {
      const qp = new URLSearchParams({ key: notificationTypeState.selectedForDelete.key });
      if (effectiveInstituteId && (isSuperAdmin || normalizedRole === "admin")) qp.set("instituteId", String(effectiveInstituteId));
      await api.delete(`/academic-info/notification-types/delete?${qp.toString()}`);
      setToastMessage("Notification type deleted.");
      setToastSeverity("success");
      setNotificationTypeState((p) => ({ ...p, deleteDialogOpen: false, selectedForDelete: null }));
      fetchNotificationTypes();
    } catch (err) {
      setToastMessage(err?.response?.data?.message || "Failed to delete notification type.");
      setToastSeverity("error");
    } finally {
      setNotificationTypeState((p) => ({ ...p, submitting: false }));
    }
  };

  const validateSemesterForm = () => {
    const err = {};
    const today = new Date(todayIso);
    if (!(semesterFormData.semesterName || "").trim()) err.semesterName = "Semester name is required";
    if (!(semesterFormData.academicYearId || "").toString().trim()) err.academicYearId = "Academic year is required";
    if (!(semesterFormData.startDate || "").trim()) err.startDate = "Start date is required";
    else if (sanitizeDateYear(semesterFormData.startDate) !== semesterFormData.startDate) err.startDate = "Year must be 4 digits (1000-9999)";
    else {
      const msg = dateRangeError(semesterFormData.startDate, todayIso, MAX_ISO_DATE, "Start date");
      if (msg) err.startDate = msg;
      else if (new Date(semesterFormData.startDate) < today) err.startDate = "Start date cannot be in the past";
    }
    if (!(semesterFormData.endDate || "").trim()) err.endDate = "End date is required";
    else if (sanitizeDateYear(semesterFormData.endDate) !== semesterFormData.endDate) err.endDate = "Year must be 4 digits (1000-9999)";
    else {
      const msg = dateRangeError(semesterFormData.endDate, todayIso, MAX_ISO_DATE, "End date");
      if (msg) err.endDate = msg;
      else if (new Date(semesterFormData.endDate) < today) err.endDate = "End date cannot be in the past";
    }
    if (semesterFormData.startDate && semesterFormData.endDate && new Date(semesterFormData.endDate) <= new Date(semesterFormData.startDate)) {
      err.endDate = "End date must be after start date";
    }
    if (semesterFormData.status && !["active", "completed"].includes(semesterFormData.status)) err.status = "Status must be active or completed";
    setSemesterFormErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleAddSemesterOpen = () => {
    setSelectedSemesterForEdit(null);
    setSemesterFormData({
      semesterName: "",
      academicYearId: "",
      startDate: "",
      endDate: "",
      status: "active",
    });
    setSemesterFormErrors({});
    setDialogType("semester");
    setAddDialogOpen(true);
    fetchAcademicYearsForDropdown();
  };

  const handleEditSemesterOpen = (row) => {
    setSelectedSemesterForEdit(row);
    setSemesterFormData({
      semesterName: row.semesterName ?? row.name ?? "",
      academicYearId: row.academicYearId ?? row.academicYear?.id ?? "",
      startDate: row.startDate ?? "",
      endDate: row.endDate ?? "",
      status: row.status ?? "active",
    });
    setSemesterFormErrors({});
    setDialogType("semester");
    setAddDialogOpen(true);
    fetchAcademicYearsForDropdown();
  };

  const handleCreateSemester = async () => {
    if (!validateSemesterForm()) return;
    setSubmittingSemester(true);
    try {
      const body = {
        semesterName: semesterFormData.semesterName.trim(),
        academicYearId: Number(semesterFormData.academicYearId),
        startDate: semesterFormData.startDate,
        endDate: semesterFormData.endDate,
        status: semesterFormData.status || "active",
      };
      const res = await api.post("/academic-info/semester/create", body);
      setToastMessage(res?.data?.message || "Semester created successfully.");
      setToastSeverity("success");
      setAddDialogOpen(false);
      fetchSemesters();
      fetchAcademicInfoStats();
    } catch (err) {
      setToastMessage(err?.response?.data?.message || "Failed to create semester.");
      setToastSeverity("error");
    } finally {
      setSubmittingSemester(false);
    }
  };

  const handleUpdateSemester = async () => {
    if (!selectedSemesterForEdit?.id || !validateSemesterForm()) return;
    setSubmittingSemester(true);
    try {
      const body = {
        semesterName: semesterFormData.semesterName.trim(),
        academicYearId: Number(semesterFormData.academicYearId),
        startDate: semesterFormData.startDate,
        endDate: semesterFormData.endDate,
        status: semesterFormData.status,
      };
      const res = await api.put(`/academic-info/semester/update?id=${selectedSemesterForEdit.id}`, body);
      setToastMessage(res?.data?.message || "Semester updated successfully.");
      setToastSeverity("success");
      setAddDialogOpen(false);
      setSelectedSemesterForEdit(null);
      fetchSemesters();
      fetchAcademicInfoStats();
    } catch (err) {
      setToastMessage(err?.response?.data?.message || "Failed to update semester.");
      setToastSeverity("error");
    } finally {
      setSubmittingSemester(false);
    }
  };

  const handleDeleteSemesterOpen = (row) => {
    setSelectedSemesterForDelete(row);
    setDeleteSemesterDialogOpen(true);
  };

  const handleDeleteSemester = async () => {
    if (!selectedSemesterForDelete?.id) return;
    setSubmittingSemester(true);
    try {
      await api.delete(`/academic-info/semester/delete?id=${selectedSemesterForDelete.id}`);
      setToastMessage("Semester deleted successfully.");
      setToastSeverity("success");
      setDeleteSemesterDialogOpen(false);
      setSelectedSemesterForDelete(null);
      fetchSemesters();
      fetchAcademicInfoStats();
    } catch (err) {
      setToastMessage(err?.response?.data?.message || "Failed to delete semester.");
      setToastSeverity("error");
    } finally {
      setSubmittingSemester(false);
    }
  };

  const validateHolidayForm = () => {
    const err = {};
    const name = (holidayFormData.name || "").trim();
    if (!name) err.name = "Holiday name is required";
    else if (/\d/.test(name)) err.name = "Numbers are not allowed in holiday name";
    else if (name.length < NAME_MIN_LENGTH) err.name = `Holiday name must be at least ${NAME_MIN_LENGTH} characters`;
    else if (name.length > NAME_MAX_LENGTH) err.name = `Holiday name must be at most ${NAME_MAX_LENGTH} characters`;
    if (!(holidayFormData.startDate || "").trim()) err.startDate = "Date is required";
    else if (sanitizeDateYear(holidayFormData.startDate) !== holidayFormData.startDate) err.startDate = "Year must be 4 digits (1000-9999)";
    else {
      const msg = dateRangeError(holidayFormData.startDate, MIN_ISO_DATE, MAX_ISO_DATE, "Date");
      if (msg) err.startDate = msg;
    }
    if (holidayFormData.endDate && sanitizeDateYear(holidayFormData.endDate) !== holidayFormData.endDate) err.endDate = "Year must be 4 digits (1000-9999)";
    else if (holidayFormData.endDate) {
      const msg = dateRangeError(holidayFormData.endDate, MIN_ISO_DATE, MAX_ISO_DATE, "End date");
      if (msg) err.endDate = msg;
    }
    if (holidayFormData.startDate && holidayFormData.endDate && new Date(holidayFormData.endDate) < new Date(holidayFormData.startDate)) {
      err.endDate = "End date must be on or after start date";
    }
    setHolidayFormErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleAddHolidayOpen = () => {
    setSelectedHolidayForEdit(null);
    setHolidayFormData({ name: "", startDate: "", endDate: "", type: "National", description: "" });
    setHolidayFormErrors({});
    setDialogType("holiday");
    setAddDialogOpen(true);
  };

  const handleEditHolidayOpen = (row) => {
    setSelectedHolidayForEdit(row);
    setHolidayFormData({
      name: sanitizeNameNoNumbers(row.name ?? ""),
      startDate: row.startDate ?? row.date ?? "",
      endDate: row.endDate ?? "",
      type: normalizeHolidayType(row.type),
      description: row.description ?? "",
    });
    setHolidayFormErrors({});
    setDialogType("holiday");
    setAddDialogOpen(true);
  };

  const handleCreateHoliday = async () => {
    if (!validateHolidayForm()) return;
    setSubmittingHoliday(true);
    try {
      const body = {
        name: holidayFormData.name.trim(),
        startDate: holidayFormData.startDate,
        type: holidayFormData.type || "National",
        description: (holidayFormData.description || "").trim() || undefined,
      };
      if (holidayFormData.endDate) body.endDate = holidayFormData.endDate;
      if (effectiveInstituteId && (isSuperAdmin || normalizedRole === "admin"))
        body.instituteId = Number(effectiveInstituteId);
      const res = await api.post("/academic-info/holidays/create", body);
      setToastMessage(res?.data?.message || "Holiday created successfully.");
      setToastSeverity("success");
      setAddDialogOpen(false);
      fetchHolidays();
      fetchUpcomingHolidaysCount();
      fetchAcademicInfoStats();
    } catch (err) {
      setToastMessage(err?.response?.data?.message || "Failed to create holiday.");
      setToastSeverity("error");
    } finally {
      setSubmittingHoliday(false);
    }
  };

  const handleUpdateHoliday = async () => {
    if (!selectedHolidayForEdit?.id || !validateHolidayForm()) return;
    setSubmittingHoliday(true);
    try {
      const body = {
        name: holidayFormData.name.trim(),
        startDate: holidayFormData.startDate,
        endDate: holidayFormData.endDate || undefined,
        type: holidayFormData.type || "National",
        description: (holidayFormData.description || "").trim() || undefined,
      };
      const res = await api.put(`/academic-info/holidays/update?id=${selectedHolidayForEdit.id}`, body);
      setToastMessage(res?.data?.message || "Holiday updated successfully.");
      setToastSeverity("success");
      setAddDialogOpen(false);
      setSelectedHolidayForEdit(null);
      fetchHolidays();
      fetchUpcomingHolidaysCount();
      fetchAcademicInfoStats();
    } catch (err) {
      setToastMessage(err?.response?.data?.message || "Failed to update holiday.");
      setToastSeverity("error");
    } finally {
      setSubmittingHoliday(false);
    }
  };

  const handleDeleteHolidayOpen = (row) => {
    setSelectedHolidayForDelete(row);
    setDeleteHolidayDialogOpen(true);
  };

  const handleDeleteHoliday = async () => {
    if (!selectedHolidayForDelete?.id) return;
    setSubmittingHoliday(true);
    try {
      await api.delete(`/academic-info/holidays/delete?id=${selectedHolidayForDelete.id}`);
      setToastMessage("Holiday deleted successfully.");
      setToastSeverity("success");
      setDeleteHolidayDialogOpen(false);
      setSelectedHolidayForDelete(null);
      fetchHolidays();
      fetchUpcomingHolidaysCount();
      fetchAcademicInfoStats();
    } catch (err) {
      setToastMessage(err?.response?.data?.message || "Failed to delete holiday.");
      setToastSeverity("error");
    } finally {
      setSubmittingHoliday(false);
    }
  };

  const validateEventForm = () => {
    const err = {};
    const name = (eventFormData.name || "").trim();
    if (!name) err.name = "Event name is required";
    else if (/\d/.test(name)) err.name = "Numbers are not allowed in event name";
    else if (name.length < NAME_MIN_LENGTH) err.name = `Event name must be at least ${NAME_MIN_LENGTH} characters`;
    else if (name.length > NAME_MAX_LENGTH) err.name = `Event name must be at most ${NAME_MAX_LENGTH} characters`;
    if (!(eventFormData.startDate || "").trim()) err.startDate = "Date is required";
    else if (sanitizeDateYear(eventFormData.startDate) !== eventFormData.startDate) err.startDate = "Year must be 4 digits (1000-9999)";
    else {
      const msg = dateRangeError(eventFormData.startDate, MIN_ISO_DATE, MAX_ISO_DATE, "Date");
      if (msg) err.startDate = msg;
    }
    setEventFormErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleAddEventOpen = () => {
    setSelectedEventForEdit(null);
    setEventFormData({ name: "", startDate: "", type: "Cultural", location: "", description: "" });
    setEventFormErrors({});
    setDialogType("event");
    setAddDialogOpen(true);
  };

  const handleEditEventOpen = (row) => {
    setSelectedEventForEdit(row);
    setEventFormData({
      name: sanitizeNameNoNumbers(row.name ?? ""),
      startDate: row.startDate ?? row.date ?? "",
      type: normalizeEventType(row.type),
      location: row.location ?? row.venue ?? "",
      description: row.description ?? "",
    });
    setEventFormErrors({});
    setDialogType("event");
    setAddDialogOpen(true);
  };

  const handleCreateEvent = async () => {
    if (!validateEventForm()) return;
    setSubmittingEvent(true);
    try {
      const body = {
        name: eventFormData.name.trim(),
        startDate: eventFormData.startDate,
        type: eventFormData.type || "Cultural",
        location: (eventFormData.location || "").trim() || undefined,
        description: (eventFormData.description || "").trim() || undefined,
      };
      if (effectiveInstituteId && (isSuperAdmin || normalizedRole === "admin"))
        body.instituteId = Number(effectiveInstituteId);
      const res = await api.post("/academic-info/events/create", body);
      setToastMessage(res?.data?.message || "Event created successfully.");
      setToastSeverity("success");
      setAddDialogOpen(false);
      fetchEvents();
      fetchUpcomingEventsCount();
      fetchAcademicInfoStats();
    } catch (err) {
      setToastMessage(err?.response?.data?.message || "Failed to create event.");
      setToastSeverity("error");
    } finally {
      setSubmittingEvent(false);
    }
  };

  const handleUpdateEvent = async () => {
    if (!selectedEventForEdit?.id || !validateEventForm()) return;
    setSubmittingEvent(true);
    try {
      const body = {
        name: eventFormData.name.trim(),
        startDate: eventFormData.startDate,
        type: eventFormData.type || "Cultural",
        location: (eventFormData.location || "").trim() || undefined,
        description: (eventFormData.description || "").trim() || undefined,
      };
      const res = await api.put(`/academic-info/events/update?id=${selectedEventForEdit.id}`, body);
      setToastMessage(res?.data?.message || "Event updated successfully.");
      setToastSeverity("success");
      setAddDialogOpen(false);
      setSelectedEventForEdit(null);
      fetchEvents();
      fetchUpcomingEventsCount();
      fetchAcademicInfoStats();
    } catch (err) {
      setToastMessage(err?.response?.data?.message || "Failed to update event.");
      setToastSeverity("error");
    } finally {
      setSubmittingEvent(false);
    }
  };

  const handleDeleteEventOpen = (row) => {
    setSelectedEventForDelete(row);
    setDeleteEventDialogOpen(true);
  };

  const handleDeleteEvent = async () => {
    if (!selectedEventForDelete?.id) return;
    setSubmittingEvent(true);
    try {
      await api.delete(`/academic-info/events/delete?id=${selectedEventForDelete.id}`);
      setToastMessage("Event deleted successfully.");
      setToastSeverity("success");
      setDeleteEventDialogOpen(false);
      setSelectedEventForDelete(null);
      fetchEvents();
      fetchUpcomingEventsCount();
      fetchAcademicInfoStats();
    } catch (err) {
      setToastMessage(err?.response?.data?.message || "Failed to delete event.");
      setToastSeverity("error");
    } finally {
      setSubmittingEvent(false);
    }
  };

  const handleAdd = (type) => {
    if (type === "year") {
      handleAddYearOpen();
      return;
    }
    if (type === "semester") {
      handleAddSemesterOpen();
      return;
    }
    if (type === "holiday") {
      handleAddHolidayOpen();
      return;
    }
    if (type === "event") {
      handleAddEventOpen();
      return;
    }
    if (type === "examType") {
      setExamTypeState((p) => ({ ...p, selectedForEdit: null, form: { name: "", color: "", previousKey: "" }, errors: {} }));
      setDialogType("examType");
      setAddDialogOpen(true);
      return;
    }
    if (type === "notificationType") {
      setNotificationTypeState((p) => ({ ...p, selectedForEdit: null, form: { name: "", previousKey: "" }, errors: {} }));
      setDialogType("notificationType");
      setAddDialogOpen(true);
      return;
    }
    setDialogType(type);
    setAddDialogOpen(true);
  };

  const semesterColumns = [
    { field: "name", headerName: "Semester", width: 200 },
    { field: "academicYear", headerName: "Academic Year", width: 160 },
    { field: "startDate", headerName: "Start Date", width: 160 },
    { field: "endDate", headerName: "End Date", width: 160 },
    {
      field: "status",
      headerName: "Status",
      width: 160,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === "active" ? "success" : "default"}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 160,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => handleEditSemesterOpen(params.row)} title="Edit">
            <Edit />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDeleteSemesterOpen(params.row)} title="Delete">
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  const holidayColumns = [
    {
      field: "name",
      headerName: "Holiday",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar sx={{ bgcolor: "warning.light", width: 32, height: 32 }}>
            <Event fontSize="small" />
          </Avatar>
          <Typography variant="body2" fontWeight={500}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    { field: "date", headerName: "Date", width: 160 },
    {
      field: "type",
      headerName: "Type",
      width: 160,
      renderCell: (params) => {
        const colors = {
          National: "primary",
          Festival: "secondary",
          Religious: "info",
        };
        return (
          <Chip
            label={params.value}
            size="small"
            color={colors[params.value] || "default"}
          />
        );
      },
    },
    { field: "description", headerName: "Description", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      width: 160,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => handleEditHolidayOpen(params.row)} title="Edit">
            <Edit />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDeleteHolidayOpen(params.row)} title="Delete">
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  const eventColumns = [
    {
      field: "name",
      headerName: "Event",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar sx={{ bgcolor: "success.light", width: 32, height: 32 }}>
            <School fontSize="small" />
          </Avatar>
          <Typography variant="body2" fontWeight={500}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    { field: "date", headerName: "Date", width: 160 },
    {
      field: "type",
      headerName: "Type",
      width: 160,
      renderCell: (params) => <Chip label={params.value} size="small" />,
    },
    { field: "location", headerName: "Venue", width: 260 },
    {
      field: "actions",
      headerName: "Actions",
      width: 160,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => handleEditEventOpen(params.row)} title="Edit">
            <Edit />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDeleteEventOpen(params.row)} title="Delete">
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  const examTypeColumns = [
    { field: "key", headerName: "Key", width: 180 },
    { field: "label", headerName: "Label", flex: 1 },
    { field: "color", headerName: "Color", width: 140 },
    {
      field: "actions",
      headerName: "Actions",
      width: 160,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => { setExamTypeState((p) => ({ ...p, selectedForEdit: params.row, form: { name: params.row.label || "", color: params.row.color || "", previousKey: params.row.key || "" }, errors: {} })); setDialogType("examType"); setAddDialogOpen(true); }} title="Edit">
            <Edit />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => { setExamTypeState((p) => ({ ...p, selectedForDelete: params.row, deleteDialogOpen: true })); }} title="Delete">
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  const notificationTypeColumns = [
    { field: "key", headerName: "Key", width: 180 },
    { field: "label", headerName: "Label", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      width: 160,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => { setNotificationTypeState((p) => ({ ...p, selectedForEdit: params.row, form: { name: params.row.label || "", previousKey: params.row.key || "" }, errors: {} })); setDialogType("notificationType"); setAddDialogOpen(true); }} title="Edit">
            <Edit />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => { setNotificationTypeState((p) => ({ ...p, selectedForDelete: params.row, deleteDialogOpen: true })); }} title="Delete">
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Academic Information
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage academic years, semesters, holidays, and events
          </Typography>
        </Box>
      </Box>

      {/* Stats from GET /dashboard/academic-info-stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          {
            label: "Current Academic Year",
            value: academicInfoStats?.currentAcademicYear?.academicYear ?? "—",
            color: "primary.main",
          },
          {
            label: "Current Semester",
            value: academicInfoStats?.currentSemester?.semesterName ?? "—",
            color: "secondary.main",
          },
          {
            label: "Upcoming Holidays",
            value: academicInfoStats?.upcomingHolidaysCount ?? "—",
            color: "warning.main",
          },
          {
            label: "Upcoming Events",
            value: academicInfoStats?.upcomingEventsCount ?? "—",
            color: "success.main",
          },
        ].map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: stat.color }}
                >
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card>
        <CardContent>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{ mb: 2 }}
          >
            <Tab label="Academic Years" />
            <Tab label="Semesters" />
            <Tab label="Holidays" />
            <Tab label="Events" />
            <Tab label="Exam Types" />
            <Tab label="Notification Types" />
          </Tabs>

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, mb: 2 }}>
            {activeTab === 0 && (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <TextField
                  size="small"
                  placeholder="Search academic year..."
                  value={yearSearch}
                  onChange={(e) => setYearSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 220 }}
                />
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Sort</InputLabel>
                  <Select value={yearSort} label="Sort" onChange={(e) => setYearSort(e.target.value)}>
                    <MenuItem value="id">ID</MenuItem>
                    <MenuItem value="academicYear">Academic Year</MenuItem>
                    <MenuItem value="startDate">Start Date</MenuItem>
                    <MenuItem value="endDate">End Date</MenuItem>
                    <MenuItem value="status">Status</MenuItem>
                    <MenuItem value="createdAt">Created</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 90 }}>
                  <InputLabel>Order</InputLabel>
                  <Select value={yearOrder} label="Order" onChange={(e) => setYearOrder(e.target.value)}>
                    <MenuItem value="ASC">Asc</MenuItem>
                    <MenuItem value="DESC">Desc</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}
            {activeTab === 1 && (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <TextField
                  size="small"
                  placeholder="Search semester..."
                  value={semesterSearch}
                  onChange={(e) => setSemesterSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 200 }}
                />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Academic Year</InputLabel>
                  <Select
                    value={semesterAcademicYearIdFilter}
                    label="Academic Year"
                    onChange={(e) => setSemesterAcademicYearIdFilter(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {academicYearsForDropdown.map((y) => (
                      <MenuItem key={y.id} value={y.id}>
                        {y.academicYear}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Sort</InputLabel>
                  <Select value={semesterSort} label="Sort" onChange={(e) => setSemesterSort(e.target.value)}>
                    <MenuItem value="id">ID</MenuItem>
                    <MenuItem value="semesterName">Semester Name</MenuItem>
                    <MenuItem value="startDate">Start Date</MenuItem>
                    <MenuItem value="endDate">End Date</MenuItem>
                    <MenuItem value="status">Status</MenuItem>
                    <MenuItem value="createdAt">Created</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 90 }}>
                  <InputLabel>Order</InputLabel>
                  <Select value={semesterOrder} label="Order" onChange={(e) => setSemesterOrder(e.target.value)}>
                    <MenuItem value="ASC">Asc</MenuItem>
                    <MenuItem value="DESC">Desc</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}
            {activeTab === 2 && (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <TextField
                  size="small"
                  placeholder="Search holiday..."
                  value={holidaySearch}
                  onChange={(e) => setHolidaySearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 200 }}
                />
                {/* Upcoming only - commented out
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={holidayUpcomingOnly}
                        onChange={(e) => setHolidayUpcomingOnly(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Upcoming only"
                  />
                </FormControl>
                */}
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Sort</InputLabel>
                  <Select value={holidaySort} label="Sort" onChange={(e) => setHolidaySort(e.target.value)}>
                    <MenuItem value="id">ID</MenuItem>
                    <MenuItem value="name">Name</MenuItem>
                    <MenuItem value="startDate">Date</MenuItem>
                    <MenuItem value="type">Type</MenuItem>
                    <MenuItem value="createdAt">Created</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 90 }}>
                  <InputLabel>Order</InputLabel>
                  <Select value={holidayOrder} label="Order" onChange={(e) => setHolidayOrder(e.target.value)}>
                    <MenuItem value="ASC">Asc</MenuItem>
                    <MenuItem value="DESC">Desc</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}
            {activeTab === 3 && (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <TextField
                  size="small"
                  placeholder="Search event..."
                  value={eventSearch}
                  onChange={(e) => setEventSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 200 }}
                />
                {/* Upcoming only - commented out
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={eventUpcomingOnly}
                        onChange={(e) => setEventUpcomingOnly(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Upcoming only"
                  />
                </FormControl>
                */}
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Sort</InputLabel>
                  <Select value={eventSort} label="Sort" onChange={(e) => setEventSort(e.target.value)}>
                    <MenuItem value="id">ID</MenuItem>
                    <MenuItem value="name">Name</MenuItem>
                    <MenuItem value="startDate">Date</MenuItem>
                    <MenuItem value="type">Type</MenuItem>
                    <MenuItem value="createdAt">Created</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 90 }}>
                  <InputLabel>Order</InputLabel>
                  <Select value={eventOrder} label="Order" onChange={(e) => setEventOrder(e.target.value)}>
                    <MenuItem value="ASC">Asc</MenuItem>
                    <MenuItem value="DESC">Desc</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}
            {(activeTab === 4 || activeTab === 5) && <Box />}
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() =>
                handleAdd(["year", "semester", "holiday", "event", "examType", "notificationType"][activeTab])
              }
              sx={{ ml: activeTab === 0 ? 0 : "auto" }}
            >
              Add {["Academic Year", "Semester", "Holiday", "Event", "Exam Type", "Notification Type"][activeTab]}
            </Button>
          </Box>

          {activeTab === 0 && (
            <>
              {loadingAcademicYears && <LinearProgress sx={{ mb: 1 }} />}
              <DataGrid
                rows={academicYearsRows}
                columns={yearColumns}
                getRowId={(row) => row.id}
                loading={loadingAcademicYears}
                autoHeight
                disableRowSelectionOnClick
                paginationMode="server"
                rowCount={yearPagination.total}
                paginationModel={{
                  page: yearPagination.page - 1,
                  pageSize: yearPagination.limit,
                }}
                onPaginationModelChange={(model) => {
                  setYearPagination((prev) => ({
                    ...prev,
                    page: model.page + 1,
                    limit: model.pageSize,
                  }));
                }}
                pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
                sx={{ border: "none" }}
              />
            </>
          )}
          {activeTab === 1 && (
            <>
              {loadingSemesters && <LinearProgress sx={{ mb: 1 }} />}
              <DataGrid
                rows={semestersRows}
                columns={semesterColumns}
                getRowId={(row) => row.id}
                loading={loadingSemesters}
                autoHeight
                disableRowSelectionOnClick
                paginationMode="server"
                rowCount={semesterPagination.total}
                paginationModel={{
                  page: semesterPagination.page - 1,
                  pageSize: semesterPagination.limit,
                }}
                onPaginationModelChange={(model) => {
                  setSemesterPagination((prev) => ({
                    ...prev,
                    page: model.page + 1,
                    limit: model.pageSize,
                  }));
                }}
                pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
                sx={{ border: "none" }}
              />
            </>
          )}
          {activeTab === 2 && (
            <>
              {loadingHolidays && <LinearProgress sx={{ mb: 1 }} />}
              <DataGrid
                rows={holidaysRows}
                columns={holidayColumns}
                getRowId={(row) => row.id}
                loading={loadingHolidays}
                autoHeight
                disableRowSelectionOnClick
                paginationMode="server"
                rowCount={holidayPagination.total}
                paginationModel={{
                  page: holidayPagination.page - 1,
                  pageSize: holidayPagination.limit,
                }}
                onPaginationModelChange={(model) => {
                  setHolidayPagination((prev) => ({
                    ...prev,
                    page: model.page + 1,
                    limit: model.pageSize,
                  }));
                }}
                pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
                sx={{ border: "none" }}
              />
            </>
          )}
          {activeTab === 3 && (
            <>
              {loadingEvents && <LinearProgress sx={{ mb: 1 }} />}
              <DataGrid
                rows={eventsRows}
                columns={eventColumns}
                getRowId={(row) => row.id}
                loading={loadingEvents}
                autoHeight
                disableRowSelectionOnClick
                paginationMode="server"
                rowCount={eventPagination.total}
                paginationModel={{
                  page: eventPagination.page - 1,
                  pageSize: eventPagination.limit,
                }}
                onPaginationModelChange={(model) => {
                  setEventPagination((prev) => ({
                    ...prev,
                    page: model.page + 1,
                    limit: model.pageSize,
                  }));
                }}
                pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
                sx={{ border: "none" }}
              />
            </>
          )}
          {activeTab === 4 && (
            <>
              {examTypeState.loading && <LinearProgress sx={{ mb: 1 }} />}
              <DataGrid
                rows={examTypeState.rows}
                columns={examTypeColumns}
                getRowId={(row) => row.key}
                loading={examTypeState.loading}
                autoHeight
                disableRowSelectionOnClick
                paginationMode="client"
                sx={{ border: "none" }}
              />
            </>
          )}
          {activeTab === 5 && (
            <>
              {notificationTypeState.loading && <LinearProgress sx={{ mb: 1 }} />}
              <DataGrid
                rows={notificationTypeState.rows}
                columns={notificationTypeColumns}
                getRowId={(row) => row.key}
                loading={notificationTypeState.loading}
                autoHeight
                disableRowSelectionOnClick
                paginationMode="client"
                sx={{ border: "none" }}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          const busy =
            dialogType === "year"
              ? submittingYear
              : dialogType === "semester"
              ? submittingSemester
              : dialogType === "holiday"
              ? submittingHoliday
              : dialogType === "event"
              ? submittingEvent
              : dialogType === "examType"
              ? examTypeState.submitting
              : dialogType === "notificationType"
              ? notificationTypeState.submitting
              : false;
          if (!busy) setAddDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            {dialogType === "year" && selectedYearForEdit
              ? "Edit Academic Year"
              : dialogType === "semester" && selectedSemesterForEdit
              ? "Edit Semester"
              : dialogType === "holiday" && selectedHolidayForEdit
              ? "Edit Holiday"
              : dialogType === "event" && selectedEventForEdit
              ? "Edit Event"
              : dialogType === "examType" && examTypeState.selectedForEdit
              ? "Edit Exam Type"
              : dialogType === "notificationType" && notificationTypeState.selectedForEdit
              ? "Edit Notification Type"
              : `Add ${dialogType.charAt(0).toUpperCase() + dialogType.slice(1)}`}
            <IconButton
              onClick={() => {
                const busy =
                  dialogType === "year"
                    ? submittingYear
                    : dialogType === "semester"
                    ? submittingSemester
                    : dialogType === "holiday"
                    ? submittingHoliday
                    : dialogType === "event"
                    ? submittingEvent
                    : dialogType === "examType"
                    ? examTypeState.submitting
                    : dialogType === "notificationType"
                    ? notificationTypeState.submitting
                    : false;
                if (!busy) setAddDialogOpen(false);
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {dialogType === "year" && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Academic Year (e.g., 2024-2025) *"
                    value={yearFormData.academicYear}
                    onChange={(e) => {
                      const raw = String(e.target.value || "").replace(/[^\d-]/g, "");
                      const parts = raw.split("-").slice(0, 2);
                      const y1 = (parts[0] || "").slice(0, 4);
                      const y2 = parts.length > 1 ? (parts[1] || "").slice(0, 4) : "";
                      const next = y2.length > 0 || raw.includes("-") ? `${y1}-${y2}` : y1;
                      setYearFormData((p) => ({ ...p, academicYear: next }));
                    }}
                    inputProps={{ inputMode: "numeric", maxLength: 9 }}
                    error={!!yearFormErrors.academicYear}
                    helperText={yearFormErrors.academicYear}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label="Start Date *"
                    format={DATE_DISPLAY_FORMAT}
                    value={yearFormData.startDate ? new Date(yearFormData.startDate) : null}
                    onChange={(val) =>
                      handleDateChange(toApiDate(val), "startDate", setYearFormData)
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!yearFormErrors.startDate,
                        helperText: yearFormErrors.startDate,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label="End Date *"
                    format={DATE_DISPLAY_FORMAT}
                    value={yearFormData.endDate ? new Date(yearFormData.endDate) : null}
                    onChange={(val) =>
                      handleDateChange(toApiDate(val), "endDate", setYearFormData)
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!yearFormErrors.endDate,
                        helperText: yearFormErrors.endDate,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={yearFormData.status}
                      label="Status"
                      onChange={(e) =>
                        setYearFormData((p) => ({ ...p, status: e.target.value }))
                      }
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            {dialogType === "semester" && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Semester Name *"
                    value={semesterFormData.semesterName}
                    onChange={(e) =>
                      setSemesterFormData((p) => ({ ...p, semesterName: e.target.value }))
                    }
                    error={!!semesterFormErrors.semesterName}
                    helperText={semesterFormErrors.semesterName}
                    placeholder="e.g. Semester 1"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth error={!!semesterFormErrors.academicYearId}>
                    <InputLabel>Academic Year *</InputLabel>
                    <Select
                      label="Academic Year *"
                      value={semesterFormData.academicYearId}
                      onChange={(e) =>
                        setSemesterFormData((p) => ({ ...p, academicYearId: e.target.value }))
                      }
                    >
                      <MenuItem value="">Select academic year</MenuItem>
                      {academicYearsForDropdown.map((y) => (
                        <MenuItem key={y.id} value={y.id}>
                          {y.academicYear}
                        </MenuItem>
                      ))}
                    </Select>
                    {semesterFormErrors.academicYearId && (
                      <FormHelperText error>{semesterFormErrors.academicYearId}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label="Start Date *"
                    format={DATE_DISPLAY_FORMAT}
                    value={semesterFormData.startDate ? new Date(semesterFormData.startDate) : null}
                    onChange={(val) =>
                      handleDateChange(toApiDate(val), "startDate", setSemesterFormData)
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!semesterFormErrors.startDate,
                        helperText: semesterFormErrors.startDate,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label="End Date *"
                    format={DATE_DISPLAY_FORMAT}
                    value={semesterFormData.endDate ? new Date(semesterFormData.endDate) : null}
                    onChange={(val) =>
                      handleDateChange(toApiDate(val), "endDate", setSemesterFormData)
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!semesterFormErrors.endDate,
                        helperText: semesterFormErrors.endDate,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      value={semesterFormData.status}
                      onChange={(e) =>
                        setSemesterFormData((p) => ({ ...p, status: e.target.value }))
                      }
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            {dialogType === "holiday" && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Holiday Name *"
                    placeholder={`${NAME_MIN_LENGTH}-${NAME_MAX_LENGTH} characters, no numbers`}
                    inputProps={{ maxLength: NAME_MAX_LENGTH }}
                    value={holidayFormData.name}
                    onKeyDown={blockNumberKey}
                    onChange={(e) =>
                      setHolidayFormData((p) => ({ ...p, name: sanitizeNameNoNumbers(e.target.value) }))
                    }
                    error={!!holidayFormErrors.name}
                    helperText={holidayFormErrors.name}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label="Date *"
                    format={DATE_DISPLAY_FORMAT}
                    value={holidayFormData.startDate ? new Date(holidayFormData.startDate) : null}
                    onChange={(val) =>
                      handleDateChange(toApiDate(val), "startDate", setHolidayFormData)
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!holidayFormErrors.startDate,
                        helperText: holidayFormErrors.startDate,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      label="Type"
                      value={holidayFormData.type}
                      onChange={(e) =>
                        setHolidayFormData((p) => ({ ...p, type: e.target.value }))
                      }
                    >
                      <MenuItem value="National">National</MenuItem>
                      <MenuItem value="Festival">Festival</MenuItem>
                      <MenuItem value="Religious">Religious</MenuItem>
                      <MenuItem value="Institutional">Institutional</MenuItem>
                      <MenuItem value="Public">Public</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <DatePicker
                    label="End Date (optional)"
                    format={DATE_DISPLAY_FORMAT}
                    value={holidayFormData.endDate ? new Date(holidayFormData.endDate) : null}
                    onChange={(val) =>
                      handleDateChange(toApiDate(val), "endDate", setHolidayFormData)
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!holidayFormErrors.endDate,
                        helperText: holidayFormErrors.endDate,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={2}
                    value={holidayFormData.description}
                    onChange={(e) =>
                      setHolidayFormData((p) => ({ ...p, description: e.target.value }))
                    }
                  />
                </Grid>
              </>
            )}
            {dialogType === "event" && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Event Name *"
                    placeholder={`${NAME_MIN_LENGTH}-${NAME_MAX_LENGTH} characters, no numbers`}
                    inputProps={{ maxLength: NAME_MAX_LENGTH }}
                    value={eventFormData.name}
                    onKeyDown={blockNumberKey}
                    onChange={(e) =>
                      setEventFormData((p) => ({ ...p, name: sanitizeNameNoNumbers(e.target.value) }))
                    }
                    error={!!eventFormErrors.name}
                    helperText={eventFormErrors.name}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label="Date *"
                    format={DATE_DISPLAY_FORMAT}
                    value={eventFormData.startDate ? new Date(eventFormData.startDate) : null}
                    onChange={(val) =>
                      handleDateChange(toApiDate(val), "startDate", setEventFormData)
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!eventFormErrors.startDate,
                        helperText: eventFormErrors.startDate,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      label="Type"
                      value={eventFormData.type}
                      onChange={(e) =>
                        setEventFormData((p) => ({ ...p, type: e.target.value }))
                      }
                    >
                      <MenuItem value="Cultural">Cultural</MenuItem>
                      <MenuItem value="Technical">Technical</MenuItem>
                      <MenuItem value="Sports">Sports</MenuItem>
                      <MenuItem value="Academic">Academic</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Venue"
                    value={eventFormData.location}
                    onChange={(e) =>
                      setEventFormData((p) => ({ ...p, location: e.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={2}
                    value={eventFormData.description}
                    onChange={(e) =>
                      setEventFormData((p) => ({ ...p, description: e.target.value }))
                    }
                  />
                </Grid>
              </>
            )}
            {dialogType === "examType" && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Name *"
                    value={examTypeState.form.name}
                    onChange={(e) => setExamTypeState((p) => ({ ...p, form: { ...p.form, name: e.target.value } }))}
                    error={!!examTypeState.errors.name}
                    helperText={examTypeState.errors.name}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Color</InputLabel>
                    <Select
                      label="Color"
                      value={examTypeState.form.color || ""}
                      onChange={(e) => setExamTypeState((p) => ({ ...p, form: { ...p.form, color: e.target.value } }))}
                    >
                      <MenuItem value="">Default</MenuItem>
                      <MenuItem value="default">Default (grey)</MenuItem>
                      <MenuItem value="primary">Primary</MenuItem>
                      <MenuItem value="secondary">Secondary</MenuItem>
                      <MenuItem value="info">Info</MenuItem>
                      <MenuItem value="success">Success</MenuItem>
                      <MenuItem value="warning">Warning</MenuItem>
                      <MenuItem value="error">Error</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            {dialogType === "notificationType" && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Name *"
                    value={notificationTypeState.form.name}
                    onChange={(e) => setNotificationTypeState((p) => ({ ...p, form: { ...p.form, name: e.target.value } }))}
                    error={!!notificationTypeState.errors.name}
                    helperText={notificationTypeState.errors.name}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddDialogOpen(false)}
            disabled={
              dialogType === "year"
                ? submittingYear
                : dialogType === "semester"
                ? submittingSemester
                : dialogType === "holiday"
                ? submittingHoliday
                : dialogType === "event"
                ? submittingEvent
                : dialogType === "examType"
                ? examTypeState.submitting
                : dialogType === "notificationType"
                ? notificationTypeState.submitting
                : false
            }
          >
            Cancel
          </Button>
          {dialogType === "year" && (
            <Button
              variant="contained"
              onClick={selectedYearForEdit ? handleUpdateYear : handleCreateYear}
              disabled={submittingYear}
              startIcon={submittingYear ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {submittingYear ? "Saving…" : selectedYearForEdit ? "Save" : "Add"}
            </Button>
          )}
          {dialogType === "semester" && (
            <Button
              variant="contained"
              onClick={selectedSemesterForEdit ? handleUpdateSemester : handleCreateSemester}
              disabled={submittingSemester}
              startIcon={submittingSemester ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {submittingSemester ? "Saving…" : selectedSemesterForEdit ? "Save" : "Add"}
            </Button>
          )}
          {dialogType === "holiday" && (
            <Button
              variant="contained"
              onClick={selectedHolidayForEdit ? handleUpdateHoliday : handleCreateHoliday}
              disabled={submittingHoliday}
              startIcon={submittingHoliday ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {submittingHoliday ? "Saving…" : selectedHolidayForEdit ? "Save" : "Add"}
            </Button>
          )}
          {dialogType === "event" && (
            <Button
              variant="contained"
              onClick={selectedEventForEdit ? handleUpdateEvent : handleCreateEvent}
              disabled={submittingEvent}
              startIcon={submittingEvent ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {submittingEvent ? "Saving…" : selectedEventForEdit ? "Save" : "Add"}
            </Button>
          )}
          {dialogType === "examType" && (
            <Button
              variant="contained"
              onClick={handleUpsertExamType}
              disabled={examTypeState.submitting}
              startIcon={examTypeState.submitting ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {examTypeState.submitting ? "Saving…" : examTypeState.selectedForEdit ? "Save" : "Add"}
            </Button>
          )}
          {dialogType === "notificationType" && (
            <Button
              variant="contained"
              onClick={handleUpsertNotificationType}
              disabled={notificationTypeState.submitting}
              startIcon={notificationTypeState.submitting ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {notificationTypeState.submitting ? "Saving…" : notificationTypeState.selectedForEdit ? "Save" : "Add"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Academic Year confirmation */}
      <Dialog
        open={deleteYearDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          if (!submittingYear) setDeleteYearDialogOpen(false);
        }}
      >
        <DialogTitle>Delete Academic Year</DialogTitle>
        <DialogContent>
          {selectedYearForDelete && (
            <Typography>
              Are you sure you want to delete the academic year{" "}
              <strong>{selectedYearForDelete.academicYear || selectedYearForDelete.year}</strong>?
              This action cannot be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteYearDialogOpen(false)} disabled={submittingYear}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteYear}
            disabled={submittingYear}
            startIcon={submittingYear ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {submittingYear ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Semester confirmation */}
      <Dialog
        open={deleteSemesterDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          if (!submittingSemester) setDeleteSemesterDialogOpen(false);
        }}
      >
        <DialogTitle>Delete Semester</DialogTitle>
        <DialogContent>
          {selectedSemesterForDelete && (
            <Typography>
              Are you sure you want to delete the semester{" "}
              <strong>{selectedSemesterForDelete.semesterName || selectedSemesterForDelete.name}</strong>?
              This action cannot be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSemesterDialogOpen(false)} disabled={submittingSemester}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteSemester}
            disabled={submittingSemester}
            startIcon={submittingSemester ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {submittingSemester ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Holiday confirmation */}
      <Dialog
        open={deleteHolidayDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          if (!submittingHoliday) setDeleteHolidayDialogOpen(false);
        }}
      >
        <DialogTitle>Delete Holiday</DialogTitle>
        <DialogContent>
          {selectedHolidayForDelete && (
            <Typography>
              Are you sure you want to delete the holiday{" "}
              <strong>{selectedHolidayForDelete.name}</strong>?
              This action cannot be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteHolidayDialogOpen(false)} disabled={submittingHoliday}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteHoliday}
            disabled={submittingHoliday}
            startIcon={submittingHoliday ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {submittingHoliday ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Event confirmation */}
      <Dialog
        open={deleteEventDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          if (!submittingEvent) setDeleteEventDialogOpen(false);
        }}
      >
        <DialogTitle>Delete Event</DialogTitle>
        <DialogContent>
          {selectedEventForDelete && (
            <Typography>
              Are you sure you want to delete the event{" "}
              <strong>{selectedEventForDelete.name}</strong>?
              This action cannot be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteEventDialogOpen(false)} disabled={submittingEvent}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteEvent}
            disabled={submittingEvent}
            startIcon={submittingEvent ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {submittingEvent ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={examTypeState.deleteDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          if (!examTypeState.submitting) setExamTypeState((p) => ({ ...p, deleteDialogOpen: false }));
        }}
      >
        <DialogTitle>Delete Exam Type</DialogTitle>
        <DialogContent>
          {examTypeState.selectedForDelete && (
            <Typography>
              Are you sure you want to delete <strong>{examTypeState.selectedForDelete.key}</strong>?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExamTypeState((p) => ({ ...p, deleteDialogOpen: false }))} disabled={examTypeState.submitting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteExamType} disabled={examTypeState.submitting} startIcon={examTypeState.submitting ? <CircularProgress size={16} color="inherit" /> : null}>
            {examTypeState.submitting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={notificationTypeState.deleteDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          if (!notificationTypeState.submitting) setNotificationTypeState((p) => ({ ...p, deleteDialogOpen: false }));
        }}
      >
        <DialogTitle>Delete Notification Type</DialogTitle>
        <DialogContent>
          {notificationTypeState.selectedForDelete && (
            <Typography>
              Are you sure you want to delete <strong>{notificationTypeState.selectedForDelete.key}</strong>?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotificationTypeState((p) => ({ ...p, deleteDialogOpen: false }))} disabled={notificationTypeState.submitting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteNotificationType} disabled={notificationTypeState.submitting} startIcon={notificationTypeState.submitting ? <CircularProgress size={16} color="inherit" /> : null}>
            {notificationTypeState.submitting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Toast
        open={!!toastMessage}
        message={toastMessage}
        severity={toastSeverity}
        onClose={() => setToastMessage(null)}
      />
    </Box>
  );
};

export default AcademicInfo;
